import { Injectable } from '@angular/core';
import * as pdfjsLib from 'pdfjs-dist';

/**
 * Renders page 1 of a PDF to a PNG data URL so a PDF design can be shown in a plain `<img>`.
 *
 * A browser cannot decode a PDF inside an `<img>` — it fails the load and shows the broken-image
 * box. Rasterizing here is what lets PDF designs sit alongside PNG/JPG/SVG designs in the same
 * grid markup instead of needing a separate embed per card.
 *
 * The PDF bytes are fetched by pdf.js over XHR, so this depends on CORS being allowed on the
 * storage account. It is (the account echoes back any Origin), but a failure is non-fatal: the
 * promise resolves to null and callers fall back to the file-type tile.
 */
@Injectable({ providedIn: 'root' })
export class PdfThumbnailService {
  // Rendered thumbnails keyed by source URL. The saved-designs grid re-renders on every search
  // and page append, so without this the same document would be fetched and rasterized repeatedly.
  private readonly cache = new Map<string, string | null>();

  // In-flight renders keyed by source URL, so N cards pointing at one PDF issue one fetch.
  private readonly pending = new Map<string, Promise<string | null>>();

  private workerConfigured = false;

  /** True when the URL points at a PDF. Query strings / SAS tokens are stripped before matching. */
  isPdfUrl(url: string): boolean {
    if (!url) { return false; }
    return url.split(/[?#]/)[0].toLowerCase().endsWith('.pdf');
  }

  /** Thumbnail already rendered for this URL, or undefined if it has not been requested yet. */
  peek(url: string): string | null | undefined {
    return this.cache.get(url);
  }

  /**
   * Renders page 1 of `url` and resolves to a PNG data URL, or null if it cannot be rendered.
   *
   * @param maxWidth Width in CSS pixels to rasterize at. Page height follows the page's aspect
   *                 ratio. Keep this near the display size — cost scales with the pixel count.
   */
  async render(url: string, maxWidth: number = 400): Promise<string | null> {
    if (!url) { return null; }

    if (this.cache.has(url)) {
      return this.cache.get(url);
    }

    const inFlight = this.pending.get(url);
    if (inFlight) {
      return inFlight;
    }

    const job = this.renderInternal(url, maxWidth)
      .catch(() => null)
      .then((dataUrl) => {
        // Cache null too — a PDF that failed once (404, CORS, corrupt) fails the same way on
        // every later pass, and re-attempting it on each change detection would hammer the blob.
        this.cache.set(url, dataUrl);
        this.pending.delete(url);
        return dataUrl;
      });

    this.pending.set(url, job);
    return job;
  }

  /**
   * Rasterizes page 1 of a picked PDF file to a PNG data URL, for uploading in place of the PDF.
   *
   * This is what makes a PDF design behave like any other artwork downstream: Design Huddle takes
   * the stored URL as the project's media, and `getDesignDims` sizes the placement by loading that
   * URL into an `<img>` — neither works with a PDF. Converting once at upload beats special-casing
   * PDFs at every consumer.
   *
   * Deliberately does NOT paint a background, unlike the thumbnail path. This artwork gets
   * composited onto a garment, so the page's transparency has to survive — a white box behind a
   * logo would print as a white box.
   *
   * @param dpi     Target print resolution. PDF user units are 1/72", so scale = dpi / 72.
   * @param maxEdge Hard cap on the longest edge in pixels, to stay under the upload size limit
   *                on large-format pages.
   * @returns A `data:image/png;base64,…` URL, or null if the file could not be rendered.
   */
  async rasterizePdfFile(file: File, dpi: number = 300, maxEdge: number = 4000): Promise<string | null> {
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      return await this.rasterize({ data: bytes }, { dpi, maxEdge, background: null });
    } catch {
      return null;
    }
  }

  /**
   * Same as {@link rasterizePdfFile} but for a PDF already stored at a URL — used to convert a
   * design uploaded before PDFs were converted at upload time. Depends on CORS, like the
   * thumbnail path.
   */
  async rasterizePdfUrl(url: string, dpi: number = 300, maxEdge: number = 4000): Promise<string | null> {
    try {
      return await this.rasterize({ url }, { dpi, maxEdge, background: null });
    } catch {
      return null;
    }
  }

  private renderInternal(url: string, maxWidth: number): Promise<string | null> {
    // Thumbnails paint a white background: PDF pages are transparent where nothing is drawn, and
    // a PNG of transparent-on-transparent reads as an empty tile. The artwork paths above pass
    // null instead, because that transparency has to survive onto the garment.
    return this.rasterize({ url }, { maxWidth, background: '#ffffff' });
  }

  /**
   * Renders page 1 of a PDF to a PNG data URL.
   *
   * Sizing is either `maxWidth` (fit the output to a known display width) or `dpi` + `maxEdge`
   * (render for print, capped so the result stays uploadable).
   */
  private async rasterize(
    source: { url: string } | { data: Uint8Array },
    opts: { maxWidth?: number, dpi?: number, maxEdge?: number, background: string | null }
  ): Promise<string | null> {
    this.configureWorker();

    const doc = await pdfjsLib.getDocument(source as any).promise;

    try {
      const page = await doc.getPage(1);
      const baseViewport = page.getViewport({ scale: 1 });

      let scale: number;
      if (opts.maxWidth) {
        scale = opts.maxWidth / baseViewport.width;
      } else {
        // PDF user units are 1/72", so this is the points-to-pixels ratio for the target DPI.
        scale = (opts.dpi || 300) / 72;

        // Clamp on the longer edge so a large page cannot blow past the upload cap.
        const maxEdge = opts.maxEdge || 4000;
        const longestEdge = Math.max(baseViewport.width, baseViewport.height) * scale;
        if (longestEdge > maxEdge) {
          scale = scale * (maxEdge / longestEdge);
        }
      }

      const viewport = page.getViewport({ scale });
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.floor(viewport.width));
      canvas.height = Math.max(1, Math.floor(viewport.height));

      const context = canvas.getContext('2d');
      if (!context) { return null; }

      if (opts.background) {
        context.fillStyle = opts.background;
        context.fillRect(0, 0, canvas.width, canvas.height);
      }

      await page.render({ canvasContext: context, viewport }).promise;

      // Artwork paths (background: null) ask pdf.js to leave untouched pixels transparent, but
      // most logo PDFs don't have untouched pixels — they're exported from a document/artboard
      // tool as an opaque white page with the mark drawn on top. pdf.js faithfully rasterizes
      // that painted white as opaque; there is no transparency to "preserve" because the source
      // never had any. Punch it out here so the page's own white fill doesn't survive onto the
      // garment as a visible box behind the logo.
      if (opts.background === null) {
        this.stripNearWhiteBackground(context, canvas.width, canvas.height);
      }

      return canvas.toDataURL('image/png');
    } finally {
      // Frees the worker-side document. Without this the bytes of every rendered PDF stay
      // resident for the life of the page.
      await doc.destroy();
    }
  }

  /**
   * Makes near-white pixels transparent, with a linear alpha ramp between the two thresholds so
   * anti-aliased text edges fade out instead of leaving a hard, jagged cutout.
   *
   * Whiteness is the pixel's minimum channel value — a pixel only counts as "background" if R, G
   * AND B are all near 255, so a genuinely colored mark (even a light one, e.g. pale blue) is never
   * touched. Only true near-neutral-white gets removed. This is a heuristic, not a real alpha
   * matte: a logo that deliberately uses white as part of its own mark (not just page background)
   * will have that part punched out too — there is no way to distinguish "white background" from
   * "white ink" from pixel color alone.
   */
  private stripNearWhiteBackground(context: CanvasRenderingContext2D, width: number, height: number): void {
    const OPAQUE_BELOW = 235;   // pixels this white-ish or less are left fully opaque
    const TRANSPARENT_ABOVE = 250; // pixels at least this white-ish become fully transparent

    const imageData = context.getImageData(0, 0, width, height);
    const data = imageData.data;

    for (let i = 0; i < data.length; i += 4) {
      const whiteness = Math.min(data[i], data[i + 1], data[i + 2]);
      if (whiteness >= TRANSPARENT_ABOVE) {
        data[i + 3] = 0;
      } else if (whiteness > OPAQUE_BELOW) {
        const t = (whiteness - OPAQUE_BELOW) / (TRANSPARENT_ABOVE - OPAQUE_BELOW);
        data[i + 3] = Math.round(data[i + 3] * (1 - t));
      }
    }

    context.putImageData(imageData, 0, 0);
  }

  private configureWorker(): void {
    if (this.workerConfigured) { return; }

    // Checked in at src/assets/pdf/, copied verbatim from
    // node_modules/pdfjs-dist/build/pdf.worker.min.js. Vendored rather than mapped through an
    // angular.json assets entry so it needs no build-config change — an assets entry is only
    // picked up when the dev server restarts, which silently breaks every PDF preview until
    // someone works out why. RE-COPY THIS FILE when pdfjs-dist is upgraded: pdf.js throws if the
    // worker version does not match the API version.
    pdfjsLib.GlobalWorkerOptions.workerSrc = 'assets/pdf/pdf.worker.min.js';
    this.workerConfigured = true;
  }
}
